import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

const API_BASE = 'https://ubicaciones.paginasweb.cr';
const COUNTRY_CODE = 'CR';
const PROVINCE_ISO = {
  '1': 'CR-SJ', // San José
  '2': 'CR-A',  // Alajuela
  '3': 'CR-C',  // Cartago
  '4': 'CR-H',  // Heredia
  '5': 'CR-G',  // Guanacaste
  '6': 'CR-P',  // Puntarenas
  '7': 'CR-L',  // Limón
};

const tiposCedula = [
  { value: '01', label: 'Física' },
  { value: '02', label: 'Jurídica' },
  { value: '03', label: 'DIMEX' },
  { value: '04', label: 'NITE' },
];

const CustomerInfoModal = ({ userId, userEmail = '', onClose, onConfirm }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Catálogos ubicación
  const [provincias, setProvincias] = useState([]);   // [{code,name}]
  const [cantones, setCantones]   = useState([]);
  const [distritos, setDistritos] = useState([]);

  // Datos del cliente (sin apellidos)
  const [cliente, setCliente] = useState({
    name: '',
    phone: '',
    email: userEmail || '',
  });

  // Datos de facturación (ubicación + señas)
  const [ubic, setUbic] = useState({
    provinciaCode: '',
    provinciaName: '',
    cantonCode: '',
    cantonName: '',
    distritoCode: '',
    distritoName: '',
    otrasSenas: '',
    postalCode: '',
  });

  // Factura electrónica
  const [requiereFE, setRequiereFE] = useState(false);
  const [fe, setFe] = useState({
    tipo_cedula: '01',
    numero_cedula: '',
    razon_social: '',
    correo_facturacion: '',
    telefono_facturacion: '',
  });

  // Método de pago (card separada)
  const [paymentMethod, setPaymentMethod] = useState('');

  // Errores de validación
  const [errors, setErrors] = useState({});

  // ---------------- Cargar datos de cliente + provincias ----------------
  useEffect(() => {
    const loadAll = async () => {
      try {
        // Provincias
        const p = await fetch(`${API_BASE}/provincias.json`).then(r => r.json());
        const provList = Object.entries(p).map(([code, name]) => ({ code, name }));
        setProvincias(provList);

        // Cliente
        if (userId) {
          const { data, error } = await supabase.from('clientes').select('*').eq('id', userId).single();
          if (!error && data) {
            setCliente({
              name: data.nombre || '',
              phone: data.celular || '',
              email: data.correo || data.email || userEmail || '',
            });

            setRequiereFE(!!data.requiere_fe);
            setFe({
              tipo_cedula: data.tipo_cedula || '01',
              numero_cedula: data.numero_cedula || '',
              razon_social: data.razon_social || '',
              correo_facturacion: data.correo_facturacion || '',
              telefono_facturacion: data.telefono_facturacion || '',
            });

            // Precargar ubicación por NOMBRE (mapeamos a code)
            const provinciaCode = provList.find(x => x.name === (data.provincia || ''))?.code || '';
            setUbic(prev => ({
              ...prev,
              provinciaCode,
              provinciaName: data.provincia || '',
              cantonName: data.canton || '',
              distritoName: data.distrito || '',
              otrasSenas: data.otras_senas || '',
              postalCode: data.codigo_postal || data.postal_code || '',
            }));
          }
        }
      } catch (e) {
        console.error('Error cargando datos:', e);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, [userId]);

  // ---------------- Al cambiar provincia -> cargar cantones ----------------
  useEffect(() => {
    const run = async () => {
      if (!ubic.provinciaCode) { setCantones([]); setDistritos([]); return; }
      const c = await fetch(`${API_BASE}/provincia/${ubic.provinciaCode}/cantones.json`).then(r => r.json());
      const list = Object.entries(c).map(([code, name]) => ({ code, name }));
      setCantones(list);

      // Si teníamos nombre precargado, intentar mapear a code
      if (ubic.cantonName && !ubic.cantonCode) {
        const found = list.find(x => x.name === ubic.cantonName);
        if (found) setUbic(prev => ({ ...prev, cantonCode: found.code }));
      } else {
        // reset si cambió la provincia
        setUbic(prev => ({ ...prev, cantonCode: '', distritoCode: '', distritoName: '' }));
        setDistritos([]);
      }

      const provName = provincias.find(p => p.code === ubic.provinciaCode)?.name || '';
      setUbic(prev => ({ ...prev, provinciaName: provName }));
    };
    run();
    // */ eslint-disable-next-line

  }, [ubic.provinciaCode]);

  // ---------------- Al cambiar cantón -> cargar distritos ----------------
  useEffect(() => {
    const run = async () => {
      if (!ubic.provinciaCode || !ubic.cantonCode) { setDistritos([]); return; }
      const d = await fetch(`${API_BASE}/provincia/${ubic.provinciaCode}/canton/${ubic.cantonCode}/distritos.json`).then(r => r.json());
      const list = Object.entries(d).map(([code, name]) => ({ code, name }));
      setDistritos(list);

      if (ubic.distritoName && !ubic.distritoCode) {
        const found = list.find(x => x.name === ubic.distritoName);
        if (found) setUbic(prev => ({ ...prev, distritoCode: found.code }));
      } else {
        setUbic(prev => ({ ...prev, distritoCode: '' }));
      }

      const cantonName = cantones.find(c => c.code === ubic.cantonCode)?.name || '';
      setUbic(prev => ({ ...prev, cantonName }));
    };
    run();
     // */ eslint-disable-next-line

  }, [ubic.cantonCode]);

  // ---------------- Autocompletar código postal ----------------
  useEffect(() => {
    if (!ubic.provinciaCode || !ubic.cantonCode || !ubic.distritoCode) return;
    const provinceNumber = parseInt(ubic.provinciaCode, 10);
    const provinceStr = Number.isFinite(provinceNumber)
      ? String(provinceNumber)
      : String(ubic.provinciaCode || '').replace(/\D/g, '') || String(ubic.provinciaCode || '');
    const cantonStr = String(ubic.cantonCode).padStart(2, '0');
    const distritoStr = String(ubic.distritoCode).padStart(2, '0');
    const nextPostal = `${provinceStr}${cantonStr}${distritoStr}`;
    setUbic((prev) => {
      if (prev.postalCode === nextPostal) return prev;
      return { ...prev, postalCode: nextPostal };
    });
  }, [ubic.provinciaCode, ubic.cantonCode, ubic.distritoCode]);

  // ---------------- Handlers ----------------
  const setField = (setter) => (e) => {
    const { name, value } = e.target;
    setter(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' })); // limpia error del campo
  };
  const handleUbicChange = (e) => {
    const { name, value } = e.target;
    if (name === 'provinciaCode') {
      setUbic(prev => ({ ...prev, provinciaCode: value, cantonCode: '', distritoCode: '' }));
    } else if (name === 'cantonCode') {
      setUbic(prev => ({ ...prev, cantonCode: value, distritoCode: '' }));
    } else {
      setUbic(prev => ({ ...prev, [name]: value }));
    }
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // ---------------- Guardar / Confirmar ----------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!cliente.name.trim()) newErrors.name = 'Ingresa tu nombre.';
    if (!cliente.phone.trim()) newErrors.phone = 'Ingresa tu teléfono.';
    if (!cliente.email.trim()) newErrors.email = 'Ingresa tu correo.';
    if (!ubic.provinciaCode) newErrors.provinciaCode = 'Selecciona la provincia.';
    if (!ubic.cantonCode) newErrors.cantonCode = 'Selecciona el cantón.';
    if (!ubic.distritoCode) newErrors.distritoCode = 'Selecciona el distrito.';
    if (!ubic.postalCode.trim()) newErrors.postalCode = 'Ingresa el código postal.';
    if (!paymentMethod) newErrors.paymentMethod = 'Selecciona un método de pago.';

    if (requiereFE) {
      if (!fe.numero_cedula.trim()) newErrors.numero_cedula = 'Ingresa tu número de cédula.';
      if (!fe.correo_facturacion.trim()) newErrors.correo_facturacion = 'Ingresa el correo de facturación.';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // Armar dirección legible
    const address = [ubic.provinciaName, ubic.cantonName, ubic.distritoName, ubic.otrasSenas]
      .filter(Boolean)
      .join(', ');

    const provinceIso = PROVINCE_ISO[ubic.provinciaCode] || 'CR-SJ';
    const postalCode = (ubic.postalCode || '').trim();
    const billingEmail = (cliente.email || fe.correo_facturacion || userEmail || '').trim();
    const phoneNumber = cliente.phone.trim();

    // Persistir cambios en "clientes" (ubicación + FE)
    setSaving(true);
    try {
      const updatePayload = {
        // ubicación (guardamos NOMBRES)
        provincia: ubic.provinciaName || null,
        canton: ubic.cantonName || null,
        distrito: ubic.distritoName || null,
        otras_senas: (ubic.otrasSenas || '').trim() || null,
        // FE
        requiere_fe: requiereFE,
        tipo_cedula: requiereFE ? fe.tipo_cedula : null,
        numero_cedula: requiereFE ? fe.numero_cedula : null,
        razon_social: requiereFE ? fe.razon_social : null,
        correo_facturacion: requiereFE ? fe.correo_facturacion : null,
        telefono_facturacion: requiereFE ? (fe.telefono_facturacion || null) : null,
        // datos básicos útiles
        celular: cliente.phone || null,
        nombre: cliente.name || null,
      };

      const { error: upErr } = await supabase
        .from('clientes')
        .update(updatePayload)
        .eq('id', userId);

      if (upErr) {
        console.error('Error actualizando cliente:', upErr);
        alert('No pudimos guardar tus datos. Intenta nuevamente.');
        setSaving(false);
        return;
      }

      // Entregar al caller lo necesario para crear el pedido
      onConfirm({
        name: cliente.name.trim(),
        phone: phoneNumber,
        email: billingEmail,
        address,
        paymentMethod,
        billingEmail,
        billingCountry: COUNTRY_CODE,
        billingStateIso: provinceIso,
        billingZip: postalCode,
        billingPhone: phoneNumber,
        shippingCountry: COUNTRY_CODE,
        shippingStateIso: provinceIso,
        shippingZip: postalCode,
        shippingPhone: phoneNumber,
      });
    } catch (err) {
      console.error(err);
      alert('Ocurrió un error guardando los datos.');
    } finally {
      setSaving(false);
    }
  };

  // ---------------- UI ----------------
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/70 grid place-items-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">Cargando…</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-2xl font-bold text-gray-900">Confirmá tus datos</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body con scroll si es largo */}
        <div className="max-h-[80vh] overflow-y-auto px-6 py-5">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Datos del cliente */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Datos del cliente</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Nombre</label>
                  <input
                    type="text"
                    name="name"
                    value={cliente.name}
                    onChange={setField(setCliente)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">Teléfono</label>
                  <input
                    type="tel"
                    name="phone"
                    value={cliente.phone}
                    onChange={setField(setCliente)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                  />
                  {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">Correo electrónico</label>
                  <input
                    type="email"
                    name="email"
                    value={cliente.email}
                    onChange={setField(setCliente)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                </div>
              </div>
            </div>

            {/* Datos de facturación (ubicación) */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Datos de facturación</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Provincia</label>
                  <select
                    name="provinciaCode"
                    value={ubic.provinciaCode}
                    onChange={handleUbicChange}
                    className="w-full px-3 py-2 border rounded-lg bg-white"
                  >
                    <option value="">Seleccione provincia</option>
                    {provincias.map(p => (
                      <option key={p.code} value={p.code}>{p.name}</option>
                    ))}
                  </select>
                  {errors.provinciaCode && <p className="mt-1 text-xs text-red-600">{errors.provinciaCode}</p>}
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Cantón</label>
                  <select
                    name="cantonCode"
                    value={ubic.cantonCode}
                    onChange={handleUbicChange}
                    className="w-full px-3 py-2 border rounded-lg bg-white"
                    disabled={!ubic.provinciaCode}
                  >
                    <option value="">{ubic.provinciaCode ? 'Seleccione cantón' : 'Seleccione provincia'}</option>
                    {cantones.map(c => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                  {errors.cantonCode && <p className="mt-1 text-xs text-red-600">{errors.cantonCode}</p>}
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Distrito</label>
                  <select
                    name="distritoCode"
                    value={ubic.distritoCode}
                    onChange={handleUbicChange}
                    className="w-full px-3 py-2 border rounded-lg bg-white"
                    disabled={!ubic.cantonCode}
                  >
                    <option value="">{ubic.cantonCode ? 'Seleccione distrito' : 'Seleccione cantón'}</option>
                    {distritos.map(d => (
                      <option key={d.code} value={d.code}>{d.name}</option>
                    ))}
                  </select>
                  {errors.distritoCode && <p className="mt-1 text-xs text-red-600">{errors.distritoCode}</p>}
                </div>
                <div>
                <label className="block text-sm text-gray-700 mb-1">Código postal</label>
                <input
                  type="text"
                  name="postalCode"
                  value={ubic.postalCode}
                  readOnly
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                />
                {errors.postalCode && <p className="mt-1 text-xs text-red-600">{errors.postalCode}</p>}
              </div>
                <div className="md:col-span-4">
                  <label className="block text-sm text-gray-700 mb-1">Otras señas</label>
                  <input
                    type="text"
                    name="otrasSenas"
                    value={ubic.otrasSenas}
                    onChange={handleUbicChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                    placeholder="Casa #, referencia, etc."
                  />
                </div>
              </div>
            </div>

            {/* Factura electrónica */}
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="flex items-start gap-2 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={requiereFE}
                  onChange={(e) => setRequiereFE(e.target.checked)}
                  className="mt-1"
                />
                <div className="text-sm text-gray-800">
                  Necesito Factura Electrónica
                  <div className="text-xs text-gray-500">(Si estás registrado en Hacienda, marca esta casilla)</div>
                </div>
              </label>

              {requiereFE && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Tipo de cédula</label>
                    <select
                      name="tipo_cedula"
                      value={fe.tipo_cedula}
                      onChange={setField(setFe)}
                      className="w-full px-3 py-2 border rounded-lg bg-white"
                    >
                      {tiposCedula.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Número de cédula</label>
                    <input
                      type="text"
                      name="numero_cedula"
                      value={fe.numero_cedula}
                      onChange={setField(setFe)}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                    {errors.numero_cedula && <p className="mt-1 text-xs text-red-600">{errors.numero_cedula}</p>}
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Razón social (opcional)</label>
                    <input
                      type="text"
                      name="razon_social"
                      value={fe.razon_social}
                      onChange={setField(setFe)}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-700 mb-1">Correo de facturación</label>
                    <input
                      type="email"
                      name="correo_facturacion"
                      value={fe.correo_facturacion}
                      onChange={setField(setFe)}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                    {errors.correo_facturacion && <p className="mt-1 text-xs text-red-600">{errors.correo_facturacion}</p>}
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Teléfono de facturación (opcional)</label>
                    <input
                      type="tel"
                      name="telefono_facturacion"
                      value={fe.telefono_facturacion}
                      onChange={setField(setFe)}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Método de pago (card separada) */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Método de pago</h4>
              <select
                value={paymentMethod}
                onChange={(e) => { setPaymentMethod(e.target.value); setErrors(prev => ({ ...prev, paymentMethod: '' })); }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              >
                <option value="">Selecciona un método de pago</option>
                <option value="SINPE Móvil">SINPE Móvil</option>
                <option value="Transferencia bancaria">Transferencia bancaria</option>
                <option value="Pago con tarjeta">Pago con tarjeta</option>
                <option value="Emma Pay">Emma Pay</option>
              </select>
              {errors.paymentMethod && <p className="mt-1 text-xs text-red-600">{errors.paymentMethod}</p>}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-white flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-3 rounded-lg border hover:bg-gray-50">Cancelar</button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="bg-accent text-gray-900 px-6 py-3 rounded-xl hover:bg-accent/90 transition-colors font-semibold"
          >
            {saving ? 'Guardando…' : 'Confirmar compra'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerInfoModal;
